import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';

/**
 * Stands in for a screen that has not been migrated yet.
 *
 * Every route from the previous console resolves from the first phase, so the shell,
 * the guards and the navigation can all be exercised end to end before any individual
 * screen exists. Each screen replaces its placeholder in its own phase.
 */
@Component({
  selector: 'app-placeholder',
  standalone: true,
  imports: [MatCardModule, MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <mat-card>
      <mat-card-content class="placeholder">
        <mat-icon>{{ icon() }}</mat-icon>
        <h2>{{ title() }}</h2>
        <p>{{ description() }}</p>
      </mat-card-content>
    </mat-card>
  `,
  styles: `
    .placeholder {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      padding: 48px 24px;
      text-align: center;
    }
    mat-icon {
      font-size: 40px;
      width: 40px;
      height: 40px;
      opacity: 0.5;
    }
    h2 {
      margin: 0;
    }
    p {
      margin: 0;
      opacity: 0.7;
    }
  `,
})
export class PlaceholderComponent {
  private readonly route = inject(ActivatedRoute);

  protected readonly title = toSignal(
    this.route.data.pipe(map((data) => (data['title'] as string | undefined) ?? 'In arrivo')),
    { initialValue: 'In arrivo' },
  );

  protected readonly icon = toSignal(
    this.route.data.pipe(map((data) => (data['icon'] as string | undefined) ?? 'construction')),
    { initialValue: 'construction' },
  );

  protected readonly description = toSignal(
    this.route.data.pipe(
      map(
        (data) =>
          (data['description'] as string | undefined) ??
          'Questa sezione non è ancora stata migrata.',
      ),
    ),
    { initialValue: '' },
  );
}
